import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Flame, Star, Clock } from "lucide-react";
import Seo from "../components/Seo";
import { Reveal, Stagger, StaggerItem } from "../components/Reveal";
import { CategoryGlyph } from "../components/Illustrations";
import NewsletterCta from "../components/NewsletterCta";
import { RESOURCE_CATEGORIES, ARTICLES, CATEGORY_COLORS } from "../data/content";

function ArticleCard({ a, testid }) {
  const color = CATEGORY_COLORS[a.category];
  return (
    <Link to={`/resources/${a.slug}`} data-testid={testid}
      className="hub-card group flex h-full flex-col rounded-2xl p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 hover:shadow-[0_18px_40px_rgba(10,10,10,0.08)]">
      <span className="inline-flex w-fit items-center rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color, backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)` }}>{a.category}</span>
      <h3 className="font-display mt-4 text-lg font-semibold leading-snug tracking-tight text-[color:var(--ink)]">{a.title}</h3>
      <p className="mt-3 flex-1 text-sm leading-relaxed text-[color:var(--ink-soft)]">{a.excerpt}</p>
      <div className="mt-5 flex items-center justify-between border-t border-[color:var(--hairline)] pt-4 text-xs text-[color:var(--ink-soft)]">
        <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{a.readingTime}</span>
        <ArrowUpRight className="h-4 w-4 text-[color:var(--ink)] transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </Link>
  );
}

export default function Resources() {
  const [active, setActive] = useState("All");
  const filtered = active === "All" ? ARTICLES : ARTICLES.filter((a) => a.category === active);
  const featured = ARTICLES.find((a) => a.featured) || ARTICLES[0];
  const trending = ARTICLES.filter((a) => a.trending).slice(0, 4);
  const editors = ARTICLES.filter((a) => a.editorsPick).slice(0, 3);
  const grid = filtered.filter((a) => a.slug !== featured.slug || active !== "All");
  const collections = RESOURCE_CATEGORIES.filter((c) => c !== "All");

  return (
    <>
      <Seo title="Resources" description="The StUpEco Hub resource centre — practical, business-first insights on startups, growth, marketing, sales, operations, technology, AI and funding." path="/resources" />

      <section className="relative pt-32 pb-8 lg:pt-40" data-testid="resources-hero">
        <div className="grid-lines absolute inset-0 -z-10 opacity-50" />
        <div className="container-hub">
          <Reveal><span className="eyebrow"><span className="brand-bar h-1.5 w-1.5 rounded-full" /> Resource centre</span></Reveal>
          <Reveal delay={0.05}><h1 className="font-display mt-5 max-w-4xl text-4xl font-semibold leading-[1.02] tracking-[-0.03em] text-[color:var(--ink)] sm:text-5xl lg:text-6xl">Insights that help businesses grow.</h1></Reveal>
          <Reveal delay={0.1}><p className="mt-6 max-w-2xl text-base leading-relaxed text-[color:var(--ink-soft)] sm:text-lg">Practical, operator-tested reading across the disciplines that actually move companies forward.</p></Reveal>
        </div>
      </section>

      {/* Featured */}
      <section className="pb-6" data-testid="resources-featured">
        <div className="container-hub">
          <Reveal>
            <Link to={`/resources/${featured.slug}`} className="hub-card group grid grid-cols-1 overflow-hidden rounded-3xl transition-shadow duration-300 hover:shadow-[0_18px_40px_rgba(10,10,10,0.08)] lg:grid-cols-2">
              <div className="flex items-center justify-center p-10" style={{ background: `linear-gradient(135deg, color-mix(in srgb, ${CATEGORY_COLORS[featured.category]} 16%, var(--surface)), var(--surface))` }}>
                <CategoryGlyph color={CATEGORY_COLORS[featured.category]} seed={RESOURCE_CATEGORIES.indexOf(featured.category)} className="h-40 w-40" />
              </div>
              <div className="p-8 lg:p-12">
                <span className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: CATEGORY_COLORS[featured.category], backgroundColor: `color-mix(in srgb, ${CATEGORY_COLORS[featured.category]} 12%, transparent)` }}><Star className="h-3 w-3" /> Featured</span>
                <h2 className="font-display mt-5 text-2xl font-semibold leading-tight tracking-tight text-[color:var(--ink)] sm:text-3xl lg:text-4xl">{featured.title}</h2>
                <p className="mt-4 text-base leading-relaxed text-[color:var(--ink-soft)]">{featured.excerpt}</p>
                <div className="mt-6 flex items-center gap-3 text-sm text-[color:var(--ink-soft)]"><span className="font-medium text-[color:var(--ink)]">{featured.author}</span><span>·</span><span>{featured.readingTime}</span></div>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--ink)]">Read article <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" /></span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Trending + Editors picks */}
      <section className="py-10" data-testid="resources-highlights">
        <div className="container-hub grid grid-cols-1 gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-5 flex items-center gap-2"><Flame className="h-4 w-4 text-[color:var(--brand-orange)]" /><h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">Trending now</h2></div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {trending.map((a) => <ArticleCard key={a.slug} a={a} testid={`resource-trending-${a.slug}`} />)}
            </div>
          </div>
          <div className="lg:col-span-4">
            <div className="mb-5 flex items-center gap-2"><Star className="h-4 w-4 text-[color:var(--brand-teal)]" /><h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">Editor's picks</h2></div>
            <div className="hub-card divide-y divide-[color:var(--hairline)] rounded-2xl">
              {editors.map((a, i) => (
                <Link key={a.slug} to={`/resources/${a.slug}`} data-testid={`resource-editor-${a.slug}`} className="group flex items-start gap-4 p-5 transition-colors hover:bg-[color:var(--soft)]">
                  <span className="font-display text-2xl font-semibold text-[color:var(--ink-soft)]">{String(i + 1).padStart(2, "0")}</span>
                  <div>
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em]" style={{ color: CATEGORY_COLORS[a.category] }}>{a.category}</span>
                    <h3 className="font-display text-sm font-semibold leading-snug text-[color:var(--ink)]">{a.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Reading collections */}
      <section className="py-8" data-testid="resources-collections">
        <div className="container-hub">
          <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">Reading collections</h2>
          <Stagger className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-8" gap={0.04}>
            {collections.map((c, i) => (
              <StaggerItem key={c}>
                <button onClick={() => { setActive(c); window.scrollTo({ top: document.getElementById("all-articles")?.offsetTop - 90, behavior: "smooth" }); }}
                  data-testid={`resource-collection-${c.toLowerCase()}`}
                  className="hub-card group flex w-full flex-col items-center gap-3 rounded-2xl p-4 transition-transform duration-300 hover:-translate-y-1">
                  <CategoryGlyph color={CATEGORY_COLORS[c]} seed={i} className="h-14 w-14" />
                  <span className="text-sm font-semibold text-[color:var(--ink)]">{c}</span>
                </button>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* All articles + filter */}
      <section id="all-articles" className="py-10" data-testid="resources-all">
        <div className="container-hub">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <h2 className="font-display text-lg font-semibold text-[color:var(--ink)]">All articles</h2>
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Resource categories">
              {RESOURCE_CATEGORIES.map((cat) => (
                <button key={cat} role="tab" aria-selected={active === cat} data-testid={`resource-category-${cat.toLowerCase()}`} onClick={() => setActive(cat)}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors duration-200 ${active === cat ? "border-[color:var(--ink)] bg-[color:var(--invert-bg)] text-white" : "border-[color:var(--hairline)] bg-[color:var(--surface)] text-[color:var(--ink-soft)] hover:border-[color:var(--ink)] hover:text-[color:var(--ink)]"}`}>{cat}</button>
              ))}
            </div>
          </div>
          {grid.length === 0 ? (
            <p className="py-16 text-center text-[color:var(--ink-soft)]">No articles in this category yet — more coming soon.</p>
          ) : (
            <Stagger className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" gap={0.06}>
              {grid.map((a) => <StaggerItem key={a.slug}><ArticleCard a={a} testid={`resource-article-${a.slug}`} /></StaggerItem>)}
            </Stagger>
          )}
        </div>
      </section>

      <section className="py-16 lg:py-20">
        <div className="container-hub"><NewsletterCta /></div>
      </section>
    </>
  );
}
